import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { CATEGORIES, PRODUCTS } from "../data/products";
import { ArrowRight } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";
import { useLocalePath } from "../lib/useLocalePath";

export default function Products() {
    usePageTitle("meta.products");
    const { t } = useTranslation();
    const lp = useLocalePath();
    const [params, setParams] = useSearchParams();
    const initial = params.get("category") || "all";
    const [active, setActive] = useState(initial);

    // Dimensional values pass through; known tokens resolve via product_meta.
    const metaLabel = (v) => t(`product_meta.${v}`, { defaultValue: v });

    const filtered = useMemo(() => {
        if (active === "all") return PRODUCTS;
        return PRODUCTS.filter((p) => p.category === active);
    }, [active]);

    const setCategory = (key) => {
        setActive(key);
        if (key === "all") {
            params.delete("category");
        } else {
            params.set("category", key);
        }
        setParams(params, { replace: true });
    };

    return (
        <main data-testid="page-products" className="pt-32">
            <section className="px-6 md:px-12 lg:px-20 pt-12 pb-16">
                <Reveal>
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                        {t("products.overline")}
                    </p>
                    <h1 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl text-[#E8E3D9] leading-[1.02] tracking-tighter max-w-5xl">
                        {t("products.h1_a")}{" "}
                        <span className="italic text-[#C5A059]">
                            {t("products.h1_b")}
                        </span>{" "}
                        {t("products.h1_c")}
                    </h1>
                    <p className="mt-8 max-w-2xl text-[#A39E93] text-base md:text-lg font-light leading-relaxed">
                        {t("products.intro")}
                    </p>
                </Reveal>
            </section>

            {/* Filter bar */}
            <section className="px-6 md:px-12 lg:px-20 pb-12 border-b border-[#2C2C2C]">
                <div
                    data-testid="products-filter-bar"
                    className="flex flex-wrap items-center gap-x-2 gap-y-3"
                >
                    {[
                        { key: "all", label: t("products.filter_all") },
                        ...CATEGORIES.map((c) => ({
                            key: c.key,
                            label: t(`categories.${c.i18nKey}.name`),
                        })),
                    ].map((opt) => (
                        <button
                            key={opt.key}
                            data-testid={`filter-${opt.key}`}
                            onClick={() => setCategory(opt.key)}
                            className={`px-5 py-3 text-[11px] uppercase tracking-[0.25em] border transition-colors ${
                                active === opt.key
                                    ? "bg-[#C5A059] border-[#C5A059] text-[#0D0D0D]"
                                    : "border-[#2C2C2C] text-[#E8E3D9] hover:border-[#C5A059] hover:text-[#C5A059]"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Category copy when filtering */}
            {active !== "all" && (
                <section className="px-6 md:px-12 lg:px-20 py-16">
                    {CATEGORIES.filter((c) => c.key === active).map((c) => (
                        <Reveal key={c.key}>
                            <SectionHeading
                                overline={`${t(`categories.${c.i18nKey}.name`)} · HSN ${c.hsn}`}
                                title={t(`categories.${c.i18nKey}.tagline`)}
                                kicker={t(`categories.${c.i18nKey}.description`)}
                            />
                        </Reveal>
                    ))}
                </section>
            )}

            {/* Product grid */}
            <section className="px-6 md:px-12 lg:px-20 py-12 md:py-20">
                <div
                    data-testid="products-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                    {filtered.map((p, i) => {
                        const cat = CATEGORIES.find((c) => c.key === p.category);
                        const name = t(`product_items.${p.id}.name`);
                        return (
                            <Reveal key={p.id} delay={(i % 6) * 60}>
                                <article
                                    data-testid={`product-card-${p.id}`}
                                    className="group bg-[#1A1A1A] border border-[#2C2C2C] hover:border-[#C5A059] transition-colors flex flex-col h-full"
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                                        />
                                    </div>
                                    <div className="p-6 md:p-8 flex flex-col gap-4 flex-1">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                                                {cat
                                                    ? t(`categories.${cat.i18nKey}.name`)
                                                    : ""}
                                            </p>
                                            <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                                {p.origin}
                                            </p>
                                        </div>
                                        <h3 className="font-heading text-3xl text-[#E8E3D9] tracking-tight">
                                            {name}
                                        </h3>
                                        <p className="text-sm text-[#A39E93] font-light leading-relaxed italic">
                                            {t(`product_items.${p.id}.accent`)}
                                        </p>

                                        <dl className="mt-2 space-y-3 text-sm border-t border-[#2C2C2C] pt-5">
                                            <div className="grid grid-cols-3 gap-3">
                                                <dt className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93] col-span-1">
                                                    {t("products.labels.finishes")}
                                                </dt>
                                                <dd className="text-[#E8E3D9] col-span-2 font-light">
                                                    {p.finishes.map(metaLabel).join(" · ")}
                                                </dd>
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                <dt className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93] col-span-1">
                                                    {t("products.labels.sizes")}
                                                </dt>
                                                <dd className="text-[#E8E3D9] col-span-2 font-light">
                                                    {p.sizes.map(metaLabel).join(", ")}
                                                </dd>
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                <dt className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93] col-span-1">
                                                    {t("products.labels.thickness")}
                                                </dt>
                                                <dd className="text-[#E8E3D9] col-span-2 font-light">
                                                    {p.thickness.map(metaLabel).join(", ")}
                                                </dd>
                                            </div>
                                        </dl>

                                        <Link
                                            to={lp(
                                                `/contact?product=${encodeURIComponent(p.formKey)}`,
                                            )}
                                            data-testid={`product-cta-${p.id}`}
                                            className="mt-auto pt-6 inline-flex items-center gap-3 text-[#C5A059] text-[11px] uppercase tracking-[0.3em] hover:gap-5 transition-all"
                                        >
                                            {t("products.inquire_prefix")} {name}{" "}
                                            <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* CTA strip */}
            <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                <Reveal>
                    <div className="bg-[#1A1A1A] border-l-2 border-[#C5A059] p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-8">
                            <h3 className="font-heading text-3xl md:text-4xl text-[#E8E3D9] leading-tight tracking-tight">
                                {t("products.cta_custom_title")}
                            </h3>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <Link
                                to={lp("/contact")}
                                data-testid="products-cta-custom"
                                className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0D0D0D] px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#E8E3D9] transition-colors"
                            >
                                {t("products.cta_custom_button")}{" "}
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
